import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PermissionLink from '../../components/PermissionLink';
import { rowClick } from '../../utils/rowNav';
import {
    archUsable,
    deleteParam,
    getParamArchHits,
    getParamStats,
    ListParamsParams,
    listParams,
    listProcessArchs,
    MaterialKeyKind,
    ParamRow,
    ProcessArch,
    RESULT_TAGS,
    RESULT_TAG_LABELS,
    submitParam,
} from '../../api/parameter';
import { listDevices, deviceLabel, Device } from '../../api/device';
import { listMMDeviceRefs, loadMModesCached, MMMode } from '../../api/mmode';
import { loadMaterialOptions } from '../../api/material';
import {
    categoryPairFallbackLabel,
    decodeCategoryPair,
    encodeCategoryPair,
    useCategoryPairOptions,
} from '../../utils/categoryOptions';
import { listDict, DictEntry } from '../../api/dict';
import { useDictText } from '../../components/DictSelect';
import SearchSelect from '../../components/SearchSelect';
import { ApiError, alertApprovalIfPending } from '../../api/request';
import { useCan } from '../../store';
import { formatTime } from '../../utils/time';
import ParamImportModal from './ParamImportModal';
import ParamExportModal from './ParamExportModal';
import Pagination from '../../components/Pagination';
import { useSyncQueryToUrl } from '../../utils/urlState';
import { withCurrent, zhOptions } from '../../utils/searchOptions';
import { useSearchInput } from '../../utils/useSearchInput';
import {
    useSchemaFieldEnumSources,
    useSchemaFieldNames,
} from '../../utils/useSchemaFieldNames';
import { dictOptionLabel, useDictOptions } from '../../utils/dictOptions';
import { usePlanTypeText } from '../../utils/usePlanTypes';
import BatchActions from '../../components/BatchActions';
import { confirmDialog, alertDialog, promptDialog } from '../../utils/dialog';

// 参数总览 (v4 多态分表版):
// - 分类轴 = 工艺框架 (laser/knife/thermal/inkjet), 列表必须聚焦某一框架 (后端一框架一张表).
// - 材料键随框架分化: laser/knife=xcode, thermal=膜+基材双键, inkjet=二级分类.
// - 一行 = 一条参数记录 (fields+params 动态 schema), 无参数集/行两级.

const PAGE_SIZE = 20;

// 筛选下拉: 全部 + 结果标枚举 (枚举本体在 api/parameter, 与编辑页共用一份)
const RESULT_FILTER_OPTS = RESULT_TAGS.map((r) => ({
    value: r.v,
    label: r.label,
}));

// 把 device_key 拆成 (设备类型, 激光瓦数). 例: S1-10W → ["S1","10W"]; P3 → ["P3",""]
function splitDeviceKey(key: string): [string, string] {
    const m = key.match(/^(.+)-(\d+(?:\.\d+)?W)$/);
    return m ? [m[1], m[2]] : [key, ''];
}

// 结果标只读徽标. 改它在「参数管理」编辑页 (与参数值同一次编辑 + 同一条审批流),
// 列表不做行内快改 —— 改了会把已发布行踢回草稿, 从列表上直接点太容易误伤.
function resultBadge(tag: string) {
    const label = RESULT_TAG_LABELS[tag] || tag || '—';
    if (tag === 'unsupported') {
        return <span className="tag bg-rose-50 text-rose-600">{label}</span>;
    }
    if (tag === 'recommended') {
        return (
            <span className="tag bg-emerald-50 text-emerald-700">{label}</span>
        );
    }
    return <span className="tag bg-slate-100 text-slate-500">{label}</span>;
}

/** 材料键列头文案 (随框架粒度). */
export function materialKeyLabel(kind: MaterialKeyKind | undefined): string {
    if (kind === 'dual_xcode') return '膜 / 基材';
    if (kind === 'category_code') return '材料分类';
    return '材料';
}

/** params JSON → "k=v · k=v" 预览 (前 3 个键); 键优先显示字典字段名称.
 * 参-02: processingMode 的值经 mm_mode 字典映射中文 (名称为空回退原值). */
function paramsPreview(
    paramsJSON: string | undefined,
    names: Map<string, string>,
    modeNames?: Map<string, string>,
    /** 字典受控字段的值 code → label (enums_source 字段不裸显 code) */
    valueLabel?: (key: string, value: string) => string,
): string {
    if (!paramsJSON) return '';
    try {
        const obj = JSON.parse(paramsJSON) as Record<string, unknown>;
        const entries = Object.entries(obj).filter(
            ([, v]) => v !== null && v !== '' && v !== undefined,
        );
        const head = entries.slice(0, 3).map(([k, v]) => {
            const val =
                k === 'processingMode' && modeNames
                    ? modeNames.get(String(v)) || String(v)
                    : valueLabel
                      ? valueLabel(k, String(v))
                      : String(v);
            return `${names.get(k) || k}=${val}`;
        });
        const more = entries.length > 3 ? ` +${entries.length - 3}` : '';
        return head.join(' · ') + more;
    } catch {
        return '';
    }
}

function ParamSetList() {
    const canCreate = useCan('param_row', 'create');
    const canUpdate = useCan('param_row', 'update');
    const canDelete = useCan('param_row', 'delete');
    const history = useHistory();
    const location = useLocation();
    const canDeleteParam = useCan('param_row', 'delete');
    // 参数只读的人未必有材料/设备查看权: 没有就收掉对应筛选 (下拉数据来自 device/material 域接口), 表里只留编码
    const canSeeDevice = useCan('device', 'get');
    const canSeeMaterial = useCan('material', 'get');
    // 没有任何写权 → 整列「操作」不渲染, 点行进详情 (2026-09-12)
    const canManage = canCreate || canUpdate || canDelete;
    // 字段显示名 (Schema 字段字典): 参数值预览列的键用名称展示
    const fieldNames = useSchemaFieldNames();
    // ★设计A: 加工模式值 → 中文名 (参-02 值级中文, 预览列用) + 加工模式筛选候选
    const [allModes, setAllModes] = useState<MMMode[]>([]);
    useEffect(() => {
        loadMModesCached().then(setAllModes);
    }, []);
    const modeNames = useMemo(
        () =>
            new Map(
                allModes
                    .filter((m) => m.nameZh)
                    .map((m) => [m.value, m.nameZh]),
            ),
        [allModes],
    );
    const search = new URLSearchParams(location.search);
    // 支持 URL 预填: ?xcode=X (材料详情/效果图跳转), ?arch=X 预选框架,
    // ?deviceKey=X / ?subProcess=X (效果图/矩阵图"查看工艺参数"条件跳转)
    const initialMaterialKey = search.get('xcode') || '';
    const presetArch = search.get('arch') || '';
    const presetDeviceKey = search.get('deviceKey') || '';
    const presetSubProcess = search.get('subProcess') || '';
    // 全量筛选/页码同步进 URL query (见下方写回 effect), 进详情后浏览器后退可还原 (2026-07-30)
    const presetStatus = search.get('status') || '';

    const [archs, setArchs] = useState<ProcessArch[]>([]);
    const [archCode, setArchCode] = useState<string>(presetArch);
    // 设备展示名的光源段走字典 label (light_source), 不裸显 code
    const lightText = useDictText('light_source');
    // 字典受控字段的值 code → label (enums_source), 预览列不裸显 code
    const enumSources = useSchemaFieldEnumSources();
    const enumDictOpts = useDictOptions(
        Array.from(new Set(enumSources.values())),
        archCode,
    );
    // 方案类型 (独立表 plan_type) 的 code → 名称: 预览列里 planType 不走字段字典的枚举来源
    const planTypeText = usePlanTypeText(archCode);
    const [family, setFamily] = useState(search.get('family') || '');
    const [deviceKey, setDeviceKey] = useState(presetDeviceKey);
    const [materialKey, setMaterialKey] = useState(initialMaterialKey);
    // inkjet: 一级分类 (与 materialKey = 二级分类成对; 二级名只在一级下唯一)
    const [categoryType1, setCategoryType1] = useState(search.get('type1') || '');
    const [substrateXcode, setSubstrateXcode] = useState(
        search.get('substrate') || '',
    );
    // 关键词走「草稿 → 提交」: 关键词搜的是前导通配 LIKE, 一次就是一遍全表扫
    // (美服慢日志实测 78,783~123,376 行 / 0.5~1.6s, 且列表/count/跨框架命中数各扫一遍),
    // 边打边搜等于一个词打出 N 轮。回车、失焦或点「搜索」才真的查。
    const kw = useSearchInput(search.get('keyword') || '', () => setPage(1));
    const keyword = kw.value;
    const [subProcess, setSubProcess] = useState(presetSubProcess);
    const [processingMode, setProcessingMode] = useState(
        search.get('processingMode') || '',
    );
    const [resultTag, setResultTag] = useState(search.get('resultTag') || '');
    // 列表默认只看 published; 用户可切到 draft / reviewing / rejected / 全部 来找自己的草稿.
    // 从效果图/矩阵图条件跳转进来 (带 deviceKey) 时默认"全部", 避免目标参数是草稿被过滤掉.
    // URL 里 status=all 表示"全部" (空值无法与"缺省"区分)
    const [statusFilter, setStatusFilter] = useState<string>(
        presetStatus
            ? presetStatus === 'all'
                ? ''
                : presetStatus
            : presetDeviceKey
              ? ''
              : 'published',
    );

    const [devices, setDevices] = useState<Device[]>([]);
    // 列表"设备类型"列显示源数据名称 (deviceKey 悬停可见), 与设备侧展示口径一致
    const devByKey = useMemo(
        () => new Map(devices.map((d) => [d.deviceKey, d])),
        [devices],
    );
    // 机型 / 型号筛选候选 (可搜索下拉, 与材料框同款)
    const familyOpts = useMemo(
        () =>
            Array.from(new Set(devices.map((d) => d.family)))
                .filter(Boolean)
                .sort()
                .map((f) => ({ value: f, label: f })),
        [devices],
    );
    const deviceOpts = useMemo(
        () =>
            devices
                .filter((d) => !family || d.family === family)
                .map((d) => ({
                    value: d.deviceKey,
                    label: deviceLabel(d, lightText),
                })),
        [devices, family, lightText],
    );
    // 加工模式筛选候选: 选了设备只列该设备挂载的模式 (mm_device_mode), 否则全部启用模式.
    // 下拉只显示中文名, 重名才补 code.
    const [deviceModeValues, setDeviceModeValues] =
        useState<Set<string> | null>(null);
    useEffect(() => {
        if (!deviceKey) {
            setDeviceModeValues(null);
            return undefined;
        }
        let stale = false;
        listMMDeviceRefs(deviceKey)
            .then((refs) => {
                if (!stale)
                    setDeviceModeValues(new Set(refs.map((r) => r.modeValue)));
            })
            .catch(() => {
                if (!stale) setDeviceModeValues(null);
            });
        return () => {
            stale = true;
        };
    }, [deviceKey]);
    const modeOpts = useMemo(
        () =>
            zhOptions(
                allModes.filter((m) =>
                    deviceModeValues
                        ? deviceModeValues.has(m.value)
                        : m.isEnabled,
                ),
                (m) => m.value,
                (m) => m.nameZh,
            ),
        [allModes, deviceModeValues],
    );
    const [subProcessOpts, setSubProcessOpts] = useState<DictEntry[]>([]);
    const subProcessSearchOpts = useMemo(
        () => subProcessOpts.map((p) => ({ value: p.code, label: p.label })),
        [subProcessOpts],
    );
    const [list, setList] = useState<ParamRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(Number(search.get('page')) || 1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // KPI (真实统计: 各状态 pageSize=1 只取 totalCount)
    const [deviceCount, setDeviceCount] = useState(0);
    const [statCounts, setStatCounts] = useState<{
        published: number;
        draft: number;
        reviewing: number;
    }>({
        published: 0,
        draft: 0,
        reviewing: 0,
    });

    // 批量提交评审: 勾选的行 id (仅 draft/rejected 可选)
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [batchBusy, setBatchBusy] = useState(false);

    const [showImport, setShowImport] = useState(false);
    const [showExport, setShowExport] = useState(false);

    const activeArch = archs.find((a) => a.code === archCode);
    const keyKind: MaterialKeyKind = activeArch?.materialKeyKind || 'xcode';
    const catPair = useCategoryPairOptions(keyKind === 'category_code');
    const categoryValue = encodeCategoryPair(categoryType1, materialKey);

    // --- 跨框架搜索定位 (2026-09-01) ---
    // 痛点: 一框架一张表, 用户搜材料/设备时不知道参数落在哪个框架, 得逐个 Tab 试;
    // 材料详情 ?xcode= 跳转也只会落在默认框架. 有材料/设备维度筛选时拉各框架命中数:
    // chips 上显示计数, 当前框架 0 命中且别处有结果时自动切过去 (参数只归属一种框架).
    const crossArchActive = Boolean(
        keyword || materialKey || substrateXcode || deviceKey,
    );
    const [archHits, setArchHits] = useState<Record<string, number> | null>(
        null,
    );
    useEffect(() => {
        if (!crossArchActive) {
            setArchHits(null);
            return undefined;
        }
        let stale = false;
        // 筛选口径与 load() 完全一致 (substrate/subProcess 后端按各框架语义生效), badge 数 = 切过去的 totalCount
        getParamArchHits({
            keyword: keyword || undefined,
            deviceKey: deviceKey || undefined,
            family: !deviceKey ? family || undefined : undefined,
            materialKeyLike: materialKey || undefined,
            // 一级类别只在类别键框架 (inkjet) 上生效, 后端按框架粒度判断, 其余框架忽略
            type1: categoryType1 || undefined,
            substrateXcode: substrateXcode || undefined,
            subProcess: subProcess || undefined,
            processingMode: processingMode || undefined,
            resultTag: resultTag || undefined,
            status: statusFilter || undefined,
        })
            .then((m) => {
                if (!stale) setArchHits(m);
            })
            .catch(() => {
                if (!stale) setArchHits(null);
            });
        return () => {
            stale = true;
        };
    }, [
        crossArchActive,
        keyword,
        family,
        deviceKey,
        materialKey,
        categoryType1,
        substrateXcode,
        subProcess,
        processingMode,
        resultTag,
        statusFilter,
    ]);

    // 自动纠偏: 每组筛选只切一次 (ref 记签名), 不跟用户抢焦点 —— 纠偏后手动点 0 命中的 Tab
    // 不再弹回 (onSetArch 会预先登记手动选择后的签名).
    const filterSig = JSON.stringify([
        keyword,
        deviceKey,
        family,
        materialKey,
        substrateXcode,
        subProcess,
        processingMode,
        resultTag,
        statusFilter,
    ]);
    const autoSwitchedSig = useRef('');
    useEffect(() => {
        if (!archHits || !archCode || (archHits[archCode] || 0) > 0) return;
        const best = archs
            .filter((a) => archUsable(a) && (archHits[a.code] || 0) > 0)
            .sort(
                (x, y) => (archHits[y.code] || 0) - (archHits[x.code] || 0),
            )[0];
        if (!best || autoSwitchedSig.current === filterSig) return;
        autoSwitchedSig.current = filterSig;
        setArchCode(best.code);
        setPage(1);
    }, [archHits, archCode, archs, filterSig]);

    // status 非默认值才写, 空串(全部)编码为 all — 与初始化读取逻辑对应
    useSyncQueryToUrl('/param-sets', {
        arch: archCode,
        family,
        deviceKey,
        xcode: materialKey,
        type1: categoryType1,
        substrate: substrateXcode,
        keyword,
        subProcess,
        processingMode,
        resultTag,
        status: statusFilter === 'published' ? '' : statusFilter || 'all',
        page,
    });

    // 1. 启动拉框架注册表; 未预选时默认第一个已落参数表的框架
    useEffect(() => {
        listProcessArchs()
            .then((l) => {
                setArchs(l);
                if (!presetArch) {
                    const first = l.find(archUsable);
                    if (first) setArchCode(first.code);
                }
            })
            .catch(() => setArchs([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 2. 切框架: 拉设备 (机型下拉 + 覆盖设备 KPI) + 加工类型字典 + (inkjet) 分类候选
    useEffect(() => {
        if (!archCode) return;
        listDevices({ archCode, pageSize: 100 })
            .then((r) => {
                setDevices(r.list || []);
                setDeviceCount(r.totalCount || 0);
            })
            .catch(() => {
                setDevices([]);
                setDeviceCount(0);
            });
        listDict('process_type', { scope: archCode })
            .then(setSubProcessOpts)
            .catch(() => setSubProcessOpts([]));
    }, [archCode]);

    // KPI 真实统计: 各状态行数 — 后端一条 GROUP BY 一次拿全 (取代原按状态三发 pageSize=1 列表,
    // 63k 行表每发一次就是一个远程库 COUNT).
    const loadStats = useCallback(() => {
        if (!archCode) return;
        getParamStats(archCode)
            .then((m) =>
                setStatCounts({
                    published: m.published || 0,
                    draft: m.draft || 0,
                    reviewing: m.reviewing || 0,
                }),
            )
            .catch(() =>
                setStatCounts({ published: 0, draft: 0, reviewing: 0 }),
            );
    }, [archCode]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const load = useCallback(async () => {
        if (!archCode) return;
        setLoading(true);
        setError('');
        try {
            const params: ListParamsParams = {
                keyword: keyword || undefined,
                deviceKey: deviceKey || undefined,
                family: !deviceKey ? family || undefined : undefined,
                // 精确键走下拉选择; 搜索框场景走 materialKeyLike 前缀模糊 (材料详情页 ?xcode= 也走这条)
                materialKeyLike: materialKey || undefined,
                type1:
                    keyKind === 'category_code'
                        ? categoryType1 || undefined
                        : undefined,
                substrateXcode:
                    keyKind === 'dual_xcode'
                        ? substrateXcode || undefined
                        : undefined,
                subProcess: subProcess || undefined,
                processingMode: processingMode || undefined,
                resultTag: resultTag || undefined,
                status: statusFilter || undefined,
                page,
                pageSize: PAGE_SIZE,
                lite: true, // 列表不需要行级 fields/paraMatrix 大 JSON (预览用 params + 全局字段字典)
            };
            const res = await listParams(archCode, params);
            setList(res.list || []);
            setTotal(res.totalCount);
            setSelected(new Set()); // 翻页/换筛选后清空勾选, 防止误批量
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [
        archCode,
        keyword,
        family,
        deviceKey,
        materialKey,
        substrateXcode,
        subProcess,
        processingMode,
        resultTag,
        statusFilter,
        page,
        keyKind,
        categoryType1,
    ]);

    useEffect(() => {
        load();
    }, [load]);

    const onSearch = (e: React.FormEvent) => {
        e.preventDefault();
        kw.commit(); // 关键词只在这里 (以及回车/失焦) 提交; 变了会经 load 依赖触发查询
        setPage(1);
        load();
    };

    const onSetArch = (code: string) => {
        // 手动选 Tab = 用户的明确选择: 预先登记清空筛选后的签名, 自动纠偏对这组筛选不再生效
        // (签名字段顺序与 filterSig 一致; keyword/status 不随切框架清空, 其余清空)
        autoSwitchedSig.current = JSON.stringify([
            keyword,
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            statusFilter,
        ]);
        setArchCode(code);
        setFamily('');
        setDeviceKey('');
        setMaterialKey('');
        setCategoryType1('');
        setSubstrateXcode('');
        setSubProcess('');
        setProcessingMode('');
        setResultTag('');
        setPage(1);
    };

    const onOpenDetail = (id: number) => {
        history.push(
            `/param-sets/manage?arch=${encodeURIComponent(archCode)}&id=${id}`,
        );
    };

    const onNew = () => {
        history.push(`/param-sets/manage?arch=${encodeURIComponent(archCode)}`);
    };

    const onDelete = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        if (!(await confirmDialog(`确认删除参数行 #${id}?`))) return;
        try {
            const { message } = await deleteParam(archCode, id);
            alertApprovalIfPending(message);
            await load();
            loadStats();
        } catch (err) {
            const msg =
                err instanceof ApiError
                    ? `${err.code}: ${err.message}`
                    : String(err);
            alertDialog(`删除失败: ${msg}`);
        }
    };

    const onCopy = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        history.push(
            `/param-sets/manage?arch=${encodeURIComponent(archCode)}&copyFrom=${id}`,
        );
    };

    // 可提交评审 = draft / rejected; 勾选本身不限状态 (2026-09-10 批量删除复用同一套勾选),
    // 提交按钮只对其中可提交的那部分生效.
    const selectable = (p: ParamRow) =>
        p.status === 'draft' || p.status === 'rejected';
    const eligible = list.filter((p) => selectable(p) && selected.has(p.id));
    const allChecked = list.length > 0 && list.every((p) => selected.has(p.id));

    const toggleAll = () => {
        if (allChecked) {
            setSelected(new Set());
        } else {
            setSelected(new Set(list.map((p) => p.id)));
        }
    };
    const toggleOne = (id: number) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    };

    // 批量提交评审: 逐条调 submit (后端一行一任务), 汇总成功/失败
    const onBatchSubmit = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        const summary = await promptDialog(
            `批量提交 ${ids.length} 条参数评审\n提交说明 (可选, 各任务共用):`,
            '',
        );
        if (summary === null) return;
        setBatchBusy(true);
        const failed: string[] = [];
        for (const id of ids) {
            try {
                // 顺序提交: 后端每次建审批任务, 并发提交容易撞限流且失败难定位
                await submitParam(archCode, id, summary.trim());
            } catch (err) {
                const msg = err instanceof ApiError ? err.message : String(err);
                failed.push(`#${id}: ${msg}`);
            }
        }
        setBatchBusy(false);
        setSelected(new Set());
        await load();
        loadStats();
        if (failed.length > 0) {
            alertDialog(
                `提交完成: 成功 ${ids.length - failed.length} 条, 失败 ${failed.length} 条\n${failed.join('\n')}`,
            );
        } else {
            alertDialog(
                `已提交 ${ids.length} 条参数评审, 管理员可在「审批中心」批量处理`,
            );
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const kpi = [
        {
            label: '已发布',
            value: String(statCounts.published),
            desc: '本框架 published 行数',
        },
        {
            label: '草稿 / 评审中',
            value: `${statCounts.draft} / ${statCounts.reviewing}`,
            desc: '待提交 · 待审批',
        },
        { label: '覆盖设备', value: String(deviceCount), desc: '机型/刀具' },
    ];

    const colCount = 11;

    return (
        <div className="space-y-5">
            {/* 标题 + 顶部按钮 */}
            <div className="flex items-center justify-between">
                <div className="text-base font-semibold">参数总览</div>
                <div className="flex gap-2">
                    {/* 批量删除与「批量提交评审」共用同一套勾选; 删除不限行状态 */}
                    <BatchActions
                        resource="param_row"
                        entityLabel="参数行"
                        deleteKeyField="id"
                        buildDeleteData={(target) => {
                            const i = target.lastIndexOf('/');
                            return {
                                archCode: target.slice(0, i),
                                id: Number(target.slice(i + 1)),
                            };
                        }}
                        selected={Array.from(selected).map(
                            (id) => `${archCode}/${id}`,
                        )}
                        onClear={() => setSelected(new Set())}
                        onDone={load}
                    />
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => setShowImport(true)}
                        hidden={!canUpdate}
                    >
                        ⬆ 批量导入参数
                    </button>
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => setShowExport(true)}
                        disabled={!archCode}
                        title="按筛选条件导出本框架参数行为 CSV"
                    >
                        ⬇ 导出参数
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={onNew}
                        disabled={!archCode}
                        hidden={!canCreate}
                    >
                        + 新建参数
                    </button>
                </div>
            </div>

            {/* 3 KPI 卡 */}
            <div className="grid grid-cols-3 gap-4">
                {kpi.map((k) => (
                    <div key={k.label} className="card p-4">
                        <div className="text-xs text-slate-500">{k.label}</div>
                        <div className="text-2xl font-semibold mt-1">
                            {k.value}
                        </div>
                        <div className="text-[11px] text-brand-600 mt-1">
                            {k.desc}
                        </div>
                    </div>
                ))}
            </div>

            {/* 过滤卡 */}
            <div className="card p-4 space-y-3">
                {/* archBar chips: 一框架一张参数表, 必须聚焦; 未落表框架 (embroidery) 置灰 */}
                <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-xs text-slate-400 mr-1">
                        工艺框架:
                    </span>
                    {archs.map((a) => {
                        const usable = archUsable(a);
                        const hit = archHits ? archHits[a.code] || 0 : null;
                        return (
                            <span
                                key={a.code}
                                title={
                                    usable
                                        ? undefined
                                        : '该框架尚未落参数表 (注册表 param_table 为空)'
                                }
                                className={`tag ${
                                    a.code === archCode
                                        ? 'bg-brand-500 text-white cursor-pointer'
                                        : usable
                                          ? 'bg-slate-100 text-slate-600 hover:bg-slate-200 cursor-pointer'
                                          : 'bg-slate-50 text-slate-300 cursor-not-allowed'
                                }`}
                                onClick={() => usable && onSetArch(a.code)}
                            >
                                {a.icon} {a.label}
                                {/* 有材料/设备筛选时显示各框架命中数, 一眼看出参数落在哪个框架 */}
                                {usable && hit !== null && (
                                    <span
                                        className={`ml-1 font-semibold ${
                                            a.code === archCode
                                                ? ''
                                                : hit > 0
                                                  ? 'text-brand-600'
                                                  : 'opacity-50'
                                        }`}
                                    >
                                        {hit}
                                    </span>
                                )}
                            </span>
                        );
                    })}
                    {crossArchActive && archHits && (
                        <span className="text-[11px] text-slate-400">
                            (各框架命中数)
                        </span>
                    )}
                </div>

                {/* statusBar chips */}
                <div className="flex flex-wrap gap-2 items-center pt-1">
                    <span className="text-xs text-slate-400 mr-1">状态:</span>
                    {[
                        {
                            v: 'published',
                            label: '✅ 已发布',
                            cls: 'bg-emerald-500',
                        },
                        { v: 'draft', label: '📝 草稿', cls: 'bg-slate-500' },
                        {
                            v: 'reviewing',
                            label: '🟡 评审中',
                            cls: 'bg-amber-500',
                        },
                        {
                            v: 'rejected',
                            label: '❌ 已驳回',
                            cls: 'bg-rose-500',
                        },
                        { v: '', label: '全部', cls: 'bg-brand-500' },
                    ].map((s) => (
                        <span
                            key={s.v || 'all'}
                            className={`tag cursor-pointer ${
                                statusFilter === s.v
                                    ? `${s.cls} text-white`
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                            onClick={() => {
                                setStatusFilter(s.v);
                                setPage(1);
                            }}
                        >
                            {s.label}
                        </span>
                    ))}
                </div>

                {/* 过滤条件 */}
                <form
                    onSubmit={onSearch}
                    className="flex flex-wrap gap-3 items-end pt-2 border-t border-slate-100"
                >
                    <div>
                        <label className="field-label">关键词</label>
                        <input
                            type="search"
                            className="inp w-48"
                            placeholder="搜 ID / 材料键 / 设备 (回车搜索)"
                            value={kw.draft}
                            onChange={(e) => kw.setDraft(e.target.value)}
                            onKeyDown={kw.onKeyDown}
                            onBlur={kw.commit}
                        />
                    </div>
                    <div hidden={!canSeeDevice}>
                        <label className="field-label">机型</label>
                        <SearchSelect
                            className="w-36"
                            value={family}
                            onChange={(newFamily) => {
                                setFamily(newFamily);
                                if (newFamily && deviceKey) {
                                    const stillValid = devices.some(
                                        (d) =>
                                            d.family === newFamily &&
                                            d.deviceKey === deviceKey,
                                    );
                                    if (!stillValid) setDeviceKey('');
                                }
                            }}
                            placeholder="全部"
                            options={familyOpts}
                            clearable
                        />
                    </div>
                    <div hidden={!canSeeDevice}>
                        <label className="field-label">型号</label>
                        <SearchSelect
                            className="w-52"
                            value={deviceKey}
                            onChange={(v) => {
                                setDeviceKey(v);
                                const dev = devices.find(
                                    (d) => d.deviceKey === v,
                                );
                                if (dev) setFamily(dev.family);
                            }}
                            placeholder="全部"
                            options={deviceOpts}
                            clearable
                        />
                    </div>
                    {/* 材料键: 随框架粒度分化 */}
                    {!canSeeMaterial ? null : keyKind === 'category_code' ? (
                        <div>
                            <label className="field-label">材料分类</label>
                            <SearchSelect
                                className="w-52"
                                value={categoryValue}
                                onChange={(v) => {
                                    const p = decodeCategoryPair(v);
                                    setMaterialKey(p.type2);
                                    setCategoryType1(p.type1);
                                    setPage(1);
                                }}
                                placeholder="全部"
                                options={withCurrent(
                                    catPair.options,
                                    categoryValue,
                                    categoryPairFallbackLabel(categoryValue),
                                )}
                                clearable
                            />
                        </div>
                    ) : (
                        <div>
                            <label className="field-label">
                                {keyKind === 'dual_xcode'
                                    ? '膜 (主材料)'
                                    : '材料'}
                            </label>
                            <SearchSelect
                                className="w-52"
                                value={materialKey}
                                onChange={(v) => {
                                    setMaterialKey(v);
                                    setPage(1);
                                }}
                                placeholder="搜 xcode / 名称"
                                loadOptions={loadMaterialOptions}
                                clearable
                            />
                        </div>
                    )}
                    {keyKind === 'dual_xcode' && (
                        <div>
                            <label className="field-label">基材</label>
                            <SearchSelect
                                className="w-52"
                                value={substrateXcode}
                                onChange={(v) => {
                                    setSubstrateXcode(v);
                                    setPage(1);
                                }}
                                placeholder="搜 xcode / 名称"
                                loadOptions={loadMaterialOptions}
                                clearable
                            />
                        </div>
                    )}
                    <div>
                        <label className="field-label">加工模式</label>
                        <SearchSelect
                            className="w-40"
                            value={processingMode}
                            onChange={(v) => {
                                setProcessingMode(v);
                                setPage(1);
                            }}
                            placeholder="全部"
                            options={modeOpts}
                            clearable
                        />
                    </div>
                    <div>
                        <label className="field-label">加工类型</label>
                        <SearchSelect
                            className="w-40"
                            value={subProcess}
                            onChange={(v) => {
                                setSubProcess(v);
                                setPage(1);
                            }}
                            placeholder="全部"
                            options={subProcessSearchOpts}
                            clearable
                        />
                    </div>
                    <div>
                        <label className="field-label">结果</label>
                        <SearchSelect
                            className="w-28"
                            value={resultTag}
                            onChange={(v) => setResultTag(v)}
                            placeholder="全部"
                            options={RESULT_FILTER_OPTS}
                            clearable
                        />
                    </div>
                    <button
                        type="submit"
                        className="text-xs px-3 py-2 rounded bg-slate-900 text-white hover:bg-slate-700"
                    >
                        搜索
                    </button>
                    <span className="ml-auto text-xs text-slate-400">
                        当前聚焦: {activeArch?.icon} {activeArch?.label}
                    </span>
                </form>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            {/* 批量操作条: 有勾选时浮出 */}
            {selected.size > 0 && (
                <div className="card p-3 flex items-center gap-3 border-brand-200 bg-brand-50/50">
                    <span className="text-sm">
                        已勾选 <b>{selected.size}</b> 条
                        {eligible.length !== selected.size && (
                            <span className="text-slate-500">
                                {' '}
                                (其中 {eligible.length} 条可提交评审)
                            </span>
                        )}
                    </span>
                    <button
                        type="button"
                        className="btn-primary text-xs"
                        onClick={onBatchSubmit}
                        disabled={batchBusy || eligible.length === 0}
                        title={
                            eligible.length === 0
                                ? '勾选中没有草稿/已驳回的行'
                                : undefined
                        }
                    >
                        {batchBusy
                            ? '提交中…'
                            : `批量提交评审 (${eligible.length})`}
                    </button>
                    <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={() => setSelected(new Set())}
                        disabled={batchBusy}
                    >
                        取消勾选
                    </button>
                </div>
            )}

            {/* 数据表 */}
            <div className="card overflow-x-auto">
                <table className="w-full" style={{ tableLayout: 'auto' }}>
                    <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                            <th
                                className="th w-8"
                                title="勾选行做批量操作 (提交评审仅草稿/已驳回, 删除不限状态)"
                                hidden={!canDelete}
                            >
                                <input
                                    type="checkbox"
                                    checked={allChecked}
                                    disabled={list.length === 0}
                                    onChange={toggleAll}
                                    hidden={!canDelete}
                                />
                            </th>
                            <th
                                className="th font-semibold whitespace-nowrap"
                                title="参数行主键 id"
                            >
                                ID
                            </th>
                            <th
                                className="th whitespace-nowrap"
                                title="材料键 (随框架: xcode / 膜+基材 / 二级分类)"
                            >
                                {materialKeyLabel(keyKind)}
                            </th>
                            <th
                                className="th whitespace-nowrap"
                                title="设备名称 (源数据 name); 悬停单元格看 device_key"
                            >
                                设备类型
                            </th>
                            <th
                                className="th whitespace-nowrap"
                                title="激光功率/瓦数, 非激光设备为 —"
                            >
                                瓦数
                            </th>
                            <th
                                className="th whitespace-nowrap"
                                title="加工类型 (雕刻/切割/热压/...)"
                            >
                                加工类型
                            </th>
                            <th
                                className="th"
                                title="params 核心键值预览, 详情见参数编辑页"
                            >
                                核心参数
                            </th>
                            <th
                                className="th whitespace-nowrap"
                                title="最近一次更新时间"
                            >
                                最近更新
                            </th>
                            <th
                                className="th whitespace-nowrap"
                                title="推荐/不支持/待测"
                            >
                                结果
                            </th>
                            <th
                                className="th whitespace-nowrap"
                                title="关联的效果图"
                            >
                                效果图
                            </th>
                            <th
                                className="th whitespace-nowrap"
                                title="编辑 / 删除"
                                hidden={!canManage}
                            >
                                操作
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={colCount}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={colCount}
                                >
                                    该筛选下暂无数据
                                </td>
                            </tr>
                        )}
                        {list.map((p) => {
                            const [devType, watt] = splitDeviceKey(
                                p.deviceKey || '',
                            );
                            return (
                                <tr
                                    key={p.id}
                                    className="hover:bg-slate-50 cursor-pointer"
                                    title="点击查看详情"
                                    onClick={rowClick(() => onOpenDetail(p.id))}
                                >
                                    <td className="td" hidden={!canDelete}>
                                        <input
                                            type="checkbox"
                                            checked={selected.has(p.id)}
                                            title={
                                                selectable(p)
                                                    ? '勾选后可批量提交评审 / 批量删除'
                                                    : '勾选后可批量删除 (提交评审仅草稿/已驳回)'
                                            }
                                            onChange={() => toggleOne(p.id)}
                                            hidden={!canDelete}
                                        />
                                    </td>
                                    <td className="td font-mono text-xs">
                                        {p.id}
                                    </td>
                                    <td className="td">
                                        {keyKind === 'dual_xcode' ? (
                                            <span className="text-xs">
                                                {p.filmXcode ? (
                                                    <PermissionLink
                                                        resource="material"
                                                        to={`/materials/${encodeURIComponent(p.filmXcode)}`}
                                                        className="text-brand-600 hover:underline"
                                                    >
                                                        {p.filmXcode}
                                                    </PermissionLink>
                                                ) : (
                                                    <span className="text-slate-400">
                                                        —
                                                    </span>
                                                )}
                                                {p.substrateXcode ? (
                                                    <>
                                                        <span className="text-slate-300 mx-1">
                                                            +
                                                        </span>
                                                        <PermissionLink
                                                            resource="material"
                                                            to={`/materials/${encodeURIComponent(p.substrateXcode)}`}
                                                            className="text-brand-600 hover:underline"
                                                        >
                                                            {p.substrateXcode}
                                                        </PermissionLink>
                                                    </>
                                                ) : null}
                                            </span>
                                        ) : keyKind === 'category_code' ? (
                                            <span className="tag bg-slate-100 text-slate-600">
                                                {catPair.text(
                                                    p.type1,
                                                    p.type2,
                                                ) || '—'}
                                            </span>
                                        ) : p.xcode ? (
                                            <PermissionLink
                                                resource="material"
                                                to={`/materials/${encodeURIComponent(p.xcode)}`}
                                                className="text-brand-600 hover:underline"
                                            >
                                                {p.xcode}
                                            </PermissionLink>
                                        ) : (
                                            <span className="text-slate-400">
                                                —
                                            </span>
                                        )}
                                    </td>
                                    <td
                                        className="td"
                                        title={p.deviceKey || ''}
                                    >
                                        {devByKey.get(p.deviceKey || '')
                                            ?.name ||
                                            devType ||
                                            '—'}
                                    </td>
                                    <td className="td font-mono text-xs">
                                        {watt ? (
                                            <span className="tag bg-slate-100 text-slate-600">
                                                {watt}
                                            </span>
                                        ) : (
                                            <span className="text-slate-300">
                                                —
                                            </span>
                                        )}
                                    </td>
                                    {/* 加工类型显示字典 label (中文), 存储值悬停可见; 不在字典回退原值 */}
                                    <td
                                        className="td whitespace-nowrap"
                                        title={p.subProcess || ''}
                                    >
                                        {subProcessOpts.find(
                                            (o) => o.code === p.subProcess,
                                        )?.label ||
                                            p.subProcess ||
                                            '—'}
                                    </td>
                                    <td className="td font-mono text-xs text-slate-600">
                                        {paramsPreview(
                                            p.params,
                                            fieldNames,
                                            modeNames,
                                            (k, v) =>
                                                k === 'planType'
                                                    ? planTypeText(String(v))
                                                    : dictOptionLabel(
                                                          enumDictOpts,
                                                          enumSources.get(k),
                                                          v,
                                                      ),
                                        ) || '—'}
                                    </td>
                                    <td className="td text-xs text-slate-600 whitespace-nowrap">
                                        {formatTime(p.updatedAt)}
                                    </td>
                                    <td className="td whitespace-nowrap">
                                        {resultBadge(p.resultTag)}
                                        {p.overrideModeValue && (
                                            <span
                                                className="tag ml-1 bg-amber-50 text-amber-700"
                                                title="仅对本行加工模式生效, 同组其它模式用共用行"
                                            >
                                                仅此模式
                                            </span>
                                        )}
                                    </td>
                                    <td className="td">
                                        {p.effectImage || p.supportMatrix ? (
                                            <div className="flex items-center gap-1">
                                                {p.effectImage && (
                                                    <a
                                                        href={p.effectImage}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        title="效果图 (点击看大图)"
                                                        className="block w-7 h-7 rounded overflow-hidden border border-slate-200 hover:border-brand-500"
                                                    >
                                                        <img
                                                            src={p.effectImage}
                                                            alt="效果图"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </a>
                                                )}
                                                {p.supportMatrix && (
                                                    <span
                                                        className="tag bg-violet-50 text-violet-700 text-[10px]"
                                                        title="含矩阵数据"
                                                    >
                                                        矩阵
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-slate-300 text-xs">
                                                缺
                                            </span>
                                        )}
                                    </td>
                                    <td
                                        className="td text-xs whitespace-nowrap"
                                        hidden={!canManage}
                                    >
                                        <button
                                            type="button"
                                            onClick={(e) => onCopy(e, p.id)}
                                            className="text-brand-600 hover:underline mr-3"
                                            title="以本行为模板新建 (材料/设备/字段带过去, 只改数值)"
                                            hidden={!canCreate}
                                        >
                                            复制
                                        </button>
                                        {canDeleteParam && (
                                            <button
                                                type="button"
                                                className="btn-danger-text"
                                                onClick={(e) =>
                                                    onDelete(e, p.id)
                                                }
                                                title="删除参数行 (admin)"
                                                hidden={!canDelete}
                                            >
                                                删除
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* 分页器 */}
            <div className="card p-3 flex items-center justify-between text-xs">
                <span className="text-slate-500">
                    以工艺框架为一级、机型为筛选: 框机/振镜等设备差异由行级动态
                    schema 吸收, 无需新建菜单/页面.
                </span>
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    onChange={setPage}
                    total={total}
                    showEnds
                    buttonClass="px-3 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                />
            </div>

            <ParamImportModal
                open={showImport}
                onClose={() => setShowImport(false)}
                onImported={() => load()}
            />
            <ParamExportModal
                open={showExport}
                archCode={archCode}
                arch={activeArch}
                devices={devices}
                subProcessOpts={subProcessOpts}
                modeOpts={modeOpts}
                preset={{
                    keyword,
                    family,
                    deviceKey,
                    materialKey,
                    substrateXcode,
                    subProcess,
                    processingMode,
                    resultTag,
                    status: statusFilter,
                }}
                onClose={() => setShowExport(false)}
            />
        </div>
    );
}

export default ParamSetList;
